import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, VisuallyHidden } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import CreditCardFormInputs, { getErrorMessage } from 'components/credit-card-form-inputs';
import {
  AdyenCCFieldFormNames,
  IAdyenErrors,
  IAdyenState,
} from 'components/credit-card-form-inputs/adyen-credit-card-form-inputs/types';
import { MobileSpacer } from 'components/mobile-spacer';
import Modal, { ModalContent, ModalHeading } from 'components/modal';
import { ThreeDSChallengeModal } from 'components/three-ds-challenge-modal';
import useEffectOnce from 'hooks/use-effect-once';
import useErrorModal from 'hooks/use-error-modal';
import { ThreeDsMethod } from 'hooks/use-threeDS';
import { useAuthContext } from 'state/auth';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  SupportedCardType,
  defaultPaymentFieldVariation,
  defaultSupportedCardTypes,
} from 'state/launchdarkly/variations';
import { useLogger } from 'state/logger';
import { ServiceMode, useOrderContext } from 'state/order';
import { usePaymentContext } from 'state/payment';
import { ThreeDSChallengeError, ThreeDSMethodError } from 'state/payment/hooks/errors';
import { ThreeDSType } from 'state/payment/hooks/types';
import { CardType } from 'state/payment/types';
import {
  getCCFormErrors,
  initialErrorState,
  initialPaymentState,
  mapDeliveryToCCFormAddress,
  onCCFormChange,
  testCardPaymentState,
} from 'utils/payment';
import { mapPaymentMethodCardTypes } from 'utils/payment/map-payment-method-card-type';
import { routes } from 'utils/routing';

import { StyledAddCreditCardButton } from './styled';

const AddPaymentMethodModal: React.FC<{
  onCardAdded: () => void;
  onDismiss?: () => void;
}> = ({ onCardAdded, onDismiss }) => {
  const auth = useAuthContext();
  const [errors, setErrors] = useState(initialErrorState);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const payment = usePaymentContext();
  const [disableAdyenAddCard, setDisableAdyenAddCard] = useState(payment.isAdyen);
  const [threeDSChallengeTokenResponse, setThreeDSChallengeTokenResponse] = useState<string | null>(
    null
  );
  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;

  const rawSupportedCardTypes: SupportedCardType[] =
    useFlag(LaunchDarklyFlag.SUPPORTED_CARD_BRANDS_VARIATIONS) || defaultSupportedCardTypes;
  const supportedCardTypes = rawSupportedCardTypes
    .map(cardType => mapPaymentMethodCardTypes(cardType))
    .filter(Boolean) as CardType[];
  const { deliveryAddress, serviceMode } = useOrderContext();
  const location = useLocation();
  const logger = useLogger();
  const { accountToDelete } = (location?.state || {}) as { accountToDelete: string };
  const isDelivery =
    location.pathname.startsWith(routes.cart) && serviceMode === ServiceMode.DELIVERY;
  const { feCountryCode: billingCountry } = useLocale();
  const userDetailsName = auth.user?.details.name;

  const [paymentValues, setPaymentValues] = useState(
    initialPaymentState({
      isDelivery,
      deliveryAddress,
      billingCountry,
      userDetailsName,
    })
  );
  const { formatMessage } = useIntl();
  const autoFill = useFlag(LaunchDarklyFlag.AUTO_FILL_TEST_CARD);
  const navigate = useNavigate();
  const closeModal = useCallback(() => (onDismiss ? onDismiss() : navigate(-1)), [
    onDismiss,
    navigate,
  ]);

  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Vaulting Credit Card details',
  });

  useEffectOnce(() => {
    if (autoFill) {
      setPaymentValues(testCardPaymentState({ isDelivery, paymentFieldVariations }));
    }
  });

  const handleChange = useCallback(
    (name, value) => {
      const { state, formErrors } = onCCFormChange(
        name,
        value,
        paymentValues,
        errors,
        formatMessage,
        supportedCardTypes
      );
      setErrors(prevState => ({ ...prevState, ...formErrors }));
      setPaymentValues(prevState => {
        return state.billingAddressSameAsDelivery
          ? {
              ...prevState,
              ...state,
              ...mapDeliveryToCCFormAddress(deliveryAddress),
            }
          : { ...prevState, ...state };
      });
    },
    [paymentValues, errors, formatMessage, deliveryAddress, supportedCardTypes]
  );

  // @TODO: Potentially remove useCallback in follow-up task for alternative solution (Reliability Platform Task)
  const handleAdyenChange = useCallback(
    (state: IAdyenState) => {
      const {
        encryptedCardNumber,
        encryptedExpiryMonth,
        encryptedExpiryYear,
        encryptedSecurityCode,
      } = state.data.paymentMethod;
      setDisableAdyenAddCard(!state.isValid);
      setPaymentValues(prevState => {
        return {
          ...mapDeliveryToCCFormAddress(deliveryAddress),
          ...prevState,
          cardNumber: encryptedCardNumber,
          cvv: encryptedSecurityCode,
          // Temporarily being used to capture expiry errors provided by Adyen. Values are stored individually below.
          // @TODO: Remove use of empty string for this field in follow up task (Reliability Platform Task)
          expiry: '',
          // These are needed to be able to maintain separate encrypted values for addCreditAccountMutation
          encryptedExpiryMonth,
          encryptedExpiryYear,
        };
      });
    },
    [deliveryAddress]
  );

  // @TODO: Potentially remove useCallback in follow-up task for alternative solution (Reliability Platform Task)
  const handleAdyenErrors = useCallback((adyenErrors: IAdyenErrors) => {
    setErrors(prevState => {
      const field = AdyenCCFieldFormNames[adyenErrors.fieldType];
      return {
        ...prevState,
        // This provides the error messaging provided by Adyen
        [field]: adyenErrors.errorI18n,
      };
    });
  }, []);

  const addNewCard = useCallback(
    async event => {
      if (event) {
        event.preventDefault();
      }
      const { isAdyen, addPaymentMethod } = payment;
      const { hasErrors, formErrors } = getCCFormErrors(
        paymentValues,
        errors,
        formatMessage,
        paymentFieldVariations,
        billingCountry,
        isAdyen // If true, it will skip our field validation for Adyen cc fields
      );

      if (hasErrors) {
        const nameOfFirstError = [
          'nameOnCard',
          'cardNumber',
          'expiry',
          'cvv',
          'billingStreetAddress',
          'billingApt',
          'billingZip',
          'billingCity',
          'billingState',
        ].find(name => formErrors[name]);
        if (nameOfFirstError) {
          const element: HTMLInputElement | null = document.querySelector(
            `[name=${nameOfFirstError}]`
          );
          if (element) {
            element.focus();
          }
        }
        const newErrorMessage = getErrorMessage({ errors, formatMessage });
        setErrorMessage(newErrorMessage);

        return setErrors(prevState => ({ ...prevState, ...formErrors }));
      }

      const onAddCardSuccess = () => {
        onCardAdded();
        setPaymentValues(initialPaymentState({ isDelivery, deliveryAddress }));
        closeModal();
        setIsLoading(false);
      };

      const onAddCardFailure = (paymentMethodError: Error, message: string) => {
        logger.error({ error: paymentMethodError, message });
        openErrorDialog({
          message: formatMessage({ id: 'paymentAddingError' }),
          error: paymentMethodError,
        });
        setIsLoading(false);
      };

      setIsLoading(true);
      const reshapedPaymentValues = payment.transformPaymentValues({
        paymentValues,
        paymentFieldVariations,
      });
      reshapedPaymentValues.accountToDelete = accountToDelete;

      try {
        await addPaymentMethod(
          reshapedPaymentValues,
          {
            skipErrorDialogOnError: true,
          },
          threeDSChallengeTokenResponse
        );
        onAddCardSuccess();
      } catch (error) {
        if (!(error instanceof ThreeDSMethodError) && !(error instanceof ThreeDSChallengeError)) {
          onAddCardFailure(error, 'Error adding payment method');
        }
      }
    },
    [
      payment,
      paymentValues,
      errors,
      formatMessage,
      paymentFieldVariations,
      billingCountry,
      accountToDelete,
      onCardAdded,
      isDelivery,
      deliveryAddress,
      closeModal,
      logger,
      openErrorDialog,
      threeDSChallengeTokenResponse,
    ]
  );

  // Change the modal header & button text if we're re-vaulting the CC
  const modalHeading = accountToDelete
    ? formatMessage({ id: 'pleaseConfirmYourCreditCardInformation' })
    : formatMessage({ id: 'addPaymentMethod' });

  const modalBtnText = `${
    accountToDelete ? formatMessage({ id: 'confirm' }) : formatMessage({ id: 'save' })
  } & ${formatMessage({ id: 'continue' })}`;

  /**
   * Necessary to avoid re renders
   */
  const threeDSMethodFlow = useMemo(
    () => ({
      transactionId: payment.threeDS.transactionId,
      iframeContent: payment.threeDS.iframeContent,
      activeType: payment.threeDS.activeFlow,
    }),
    [payment.threeDS.transactionId, payment.threeDS.iframeContent, payment.threeDS.activeFlow]
  );

  const threeDSChallengeFlow = useMemo(
    () => ({
      activeType: payment.threeDS.activeFlow,
      acsUrl: payment.threeDS.acsUrl,
      challengeRequest: payment.threeDS.challengeRequest,
    }),
    [payment.threeDS.activeFlow, payment.threeDS.acsUrl, payment.threeDS.challengeRequest]
  );

  useEffect(() => {
    const isChallengeRequestComplete =
      payment.threeDS.isChallengeRequest() && threeDSChallengeTokenResponse;

    if (isChallengeRequestComplete) {
      addNewCard(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threeDSChallengeFlow, threeDSChallengeTokenResponse]);

  if (
    threeDSChallengeFlow.activeType === ThreeDSType.CHALLENGE &&
    threeDSChallengeFlow.acsUrl &&
    threeDSChallengeFlow.challengeRequest
  ) {
    return (
      <ThreeDSChallengeModal
        acsUrl={threeDSChallengeFlow.acsUrl}
        challengeRequestToken={threeDSChallengeFlow.challengeRequest}
        onChallengeSuccess={setThreeDSChallengeTokenResponse}
        headerIdMessage={'addCreditCardVerification'}
      />
    );
  }

  return (
    <Modal
      onDismiss={closeModal}
      mParticleEventData={{
        modalAppearanceEventMessage: 'Add payment method',
      }}
    >
      <ModalContent>
        <ModalHeading id="modal-heading" data-testid="add-payment-modal-heading">
          {modalHeading}
        </ModalHeading>
        <form onSubmit={addNewCard} noValidate>
          {errorMessage && <VisuallyHidden role="alert"> {errorMessage} </VisuallyHidden>}
          <CreditCardFormInputs
            onChange={handleChange}
            onAdyenChange={handleAdyenChange}
            onAdyenError={handleAdyenErrors}
            paymentValues={paymentValues}
            errors={errors}
            isDelivery={isDelivery}
          />
          <Box margin="1rem 0">
            <StyledAddCreditCardButton
              fullWidth
              data-testid="save-and-continue"
              isLoading={isLoading}
              disabled={!paymentValues.saveCard || disableAdyenAddCard}
            >
              {modalBtnText}
            </StyledAddCreditCardButton>
          </Box>
          <MobileSpacer />
        </form>
      </ModalContent>
      <ErrorDialog />
      {threeDSMethodFlow.transactionId && threeDSMethodFlow.iframeContent && (
        <ThreeDsMethod
          iframeContent={threeDSMethodFlow.iframeContent}
          onComplete={() => {
            payment.cleanThreeDSMethodDSFlow();
            addNewCard(null);
          }}
        />
      )}
    </Modal>
  );
};

export default AddPaymentMethodModal;
